/* eslint-disable */
import { CheckCircle, ModeEdit } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import {
	useEffect, useMemo, useState
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { modalActions } from '../../../Actions';
// eslint-disable-next-line import/no-cycle
import { ListTable } from '../../ListTable';
import { BaseModal } from '../BaseModal';
import config from '../../../Pages/Analytics/CreatePurchaseOrder/CreatePurchaseOrder.config';
import styles from './GenerateStandardPo.module.scss';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { getUnitConvertedQuantity, getUnitConvertedUnit, useProductData, handleExcelDownload, compare } from '@/Helpers';
import moment from 'moment';

const COLMAP = {
	Select: 'id',
	'Item Name': 'id',
	'SKU Code': 'clientVariantId',
	'Variant ID': 'variantId',
	Category: 'categoryName',
	'Sub Category': 'subCategoryName',
	Unit: 'unit',
	'Current Stock(CS)': 'currentStock',
	'Threshold(TH)': 'threshold',
	'Pending Order Quantity(POQ)': 'pendingOrderQty',
	'Lead Days(LD)': 'leadDays',
	'Ordering Frequency(OF)': 'orderingFrequency',
	'Daily Consumption(DC)': 'avgOrderQty',
	'Recommended Order Quantity': 'recommendedOrderQty',
	'Required Quantity': 'prePlanStock',
	MOQ: 'moq',
	'Rounded Off Ordered Quantity': 'finalProduction',
};

const SPECIAL_COLUMNS = [
	{
		title: 'Select',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Item Name',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'SKU Code',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Variant ID',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Category',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Sub Category',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Unit',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Current Stock(CS)',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Threshold(TH)',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Pending Order Quantity(POQ)',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Lead Days(LD)',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Ordering Frequency(OF)',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Daily Consumption(DC)',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Recommended Order Quantity',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Required Quantity',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'MOQ',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Rounded Off Ordered Quantity',
		config: {
			type: 'DATA',
		},
	},
];

function getNextMultiple(batchSize, quantity) {
	if (batchSize) {
		return Math.ceil(quantity / batchSize) * batchSize;
	}
	return quantity;
}

const GenerateStandardPo = (props) => {
	// const history = useHistory();
	const {
		show,
		toggleGenerateStandardPOModal,
		data,
		productsList,
		poState,
		getVarPrice,
		setPoState,
	} = props;
	const { standardPOData } = data ?? {};
	const [listData, setListData] = useState([]);
	const [finalItemList, setFinalItemList] = useState([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [isEdit, setIsEdit] = useState({});
	const dispatch = useDispatch();

	// Make sure ALL_PRODUCTS is loaded so we can resolve category /
	// subCategory for each row. Re-fetch only when missing.
	useEffect(() => {
		if (!Array.isArray(productsList) || productsList.length === 0) {
			useProductData(dispatch);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Build a fast variantId → product lookup so renderer + sort don't iterate
	// productsList for every cell.
	const productByVariantId = useMemo(() => {
		const map = new Map();
		(productsList ?? []).forEach((product) => {
			(product?.variants ?? []).forEach((variant) => {
				map.set(variant?.id, product);
			});
			// also key by product id as fallback
			if (product?.id != null && !map.has(product.id)) {
				map.set(product.id, product);
			}
		});
		return map;
	}, [productsList]);

	// Decorate every row with category / subCategory from the master product
	// list, then sort the whole array by subCategoryName (then category, then
	// product name). This runs both when standardPOData first arrives and
	// later if productByVariantId resolves after a fetch — existing edits and
	// selections are preserved across re-runs.
	useEffect(() => {
		if (!Array.isArray(standardPOData)) return;
		setListData((prev) => {
			const editsByVariantId = new Map();
			(prev ?? []).forEach((row) => {
				if (row?.variantId != null) editsByVariantId.set(row.variantId, row);
			});
			const decorated = standardPOData.map((ele) => {
				const existing = editsByVariantId.get(ele?.variantId);
				const masterProduct = productByVariantId.get(ele?.variantId)
					?? productByVariantId.get(ele?.productId);
				return {
					...ele,
					categoryName: ele?.categoryName
						?? masterProduct?.categoryName
						?? '',
					subCategoryName: ele?.subCategoryName
						?? masterProduct?.subCategoryName
						?? '',
					requiredQty: existing?.requiredQty
						?? ele?.recommendedOrderQty,
					roundedOffQty: existing?.roundedOffQty
						?? getNextMultiple(
							ele?.moq,
							ele?.recommendedOrderQty
						),
				};
			});
			return decorated.sort((a, b) => (
				compare(a?.subCategoryName, b?.subCategoryName)
				|| compare(a?.categoryName, b?.categoryName)
				|| compare(a?.productName, b?.productName)
			));
		});
		setFinalItemList((prev) => {
			if (Array.isArray(prev) && prev.length > 0) return prev;
			return standardPOData.map((ele) => ele?.variantId);
		});
	}, [standardPOData, productByVariantId]);

	const handleEditClick = (varId) => {
		setIsEdit((prev) => {
			const temp = {
				...prev,
			};
			if (temp[varId]) {
				temp[varId] = false;
			} else {
				temp[varId] = true;
			}
			return temp;
		});
	};

	const handleQtyChange = (e, varId) => {
		setListData((prev) => {
			const tempData = prev?.map((ele) => {
				if (ele?.variantId === varId) {
					return {
						...ele,
						requiredQty: e?.target?.value,
						roundedOffQty: e?.target?.value
							? getNextMultiple(
								ele?.moq,
								Number(e?.target?.value)
							)
							: e?.target?.value,
					};
				}
				return ele;
			});
			return tempData;
		});
	};

	const handleItemCheck = (item, isChecked) => {
		if (isChecked) {
			setFinalItemList((prev) => {
				return prev?.filter((itemId) => {
					return itemId !== item?.variantId;
				});
			});
		} else {
			setFinalItemList((prev) => {
				return [...prev, item?.variantId];
			});
		}
	};

	const handleSelectAll = () => {
		if (finalItemList.length === listData.length) {
			// If all items are selected, deselect all
			setFinalItemList([]);
		} else {
			// Select all items
			setFinalItemList(listData.map(item => item.variantId));
		}
	};

	const SPECIAL_ITEMS = {
		Select: (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			const isChecked = finalItemList?.includes(id);
			return (
				<input
					type='checkbox'
					onChange={() => {
						handleItemCheck(product, isChecked);
					}}
					checked={isChecked}
				/>
			);
		},
		'Item Name': (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			return `${product?.productName}${product?.variantName ? `(${product?.variantName})` : ''
				}`;
		},
		'SKU Code': (id) => {
			const row = listData?.find((prod) => prod?.variantId === id);
			// Prefer the value from the API row; fall back to the master product
			// variants list so this works even when the API doesn't include it.
			if (row?.clientVariantId != null && row?.clientVariantId !== '') {
				return row.clientVariantId;
			}
			const masterProduct = productByVariantId.get(id) ?? productByVariantId.get(row?.productId);
			const variant = masterProduct?.variants?.find((v) => v?.id === id);
			return variant?.clientVariantId ?? '-';
		},
		'Variant ID': (id) => {
			const row = listData?.find((prod) => prod?.variantId === id);
			return row?.variantId ?? id ?? '-';
		},
		Category: (id) => {
			const row = listData?.find((prod) => prod?.variantId === id);
			const masterProduct = productByVariantId.get(id) ?? productByVariantId.get(row?.productId);
			return row?.categoryName
				?? masterProduct?.categoryName
				?? '-';
		},
		'Sub Category': (id) => {
			const row = listData?.find((prod) => prod?.variantId === id);
			const masterProduct = productByVariantId.get(id) ?? productByVariantId.get(row?.productId);
			return row?.subCategoryName
				?? masterProduct?.subCategoryName
				?? '-';
		},
		Unit: (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			return product?.unit;
		},
		'Current Stock(CS)': (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			return product?.currentStock ?? '-';
		},
		'Threshold(TH)': (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			return product?.threshold ?? '-';
		},
		'Pending Order Quantity(POQ)': (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			return product?.pendingOrderQty ?? '-';
		},
		'Lead Days(LD)': (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			return product?.leadDays ?? '-';
		},
		'Ordering Frequency(OF)': (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			return product?.orderingFrequency ?? '-';
		},
		'Daily Consumption(DC)': (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			return product?.avgOrderQty;
		},
		'Recommended Order Quantity': (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			return product?.recommendedOrderQty;
		},
		'Required Quantity': (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			const recommendedValue = product?.recommendedQty > 0 ? product?.recommendedQty : '';
			return (
				<div>
					<Input
						className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
						type="number"
						placeholder="Enter Required QTY"
						value={product?.requiredQty ?? recommendedValue}
						onChange={(e) => handleQtyChange(e, product?.variantId)}
					/>
					{/* <IconButton
						onClick={() => {
							handleEditClick(product?.variantId);
						}}
					>
						<CheckCircle />
					</IconButton> */}
				</div>
			);
			// }
			// if (product?.requiredQty) {
			// 	return (
			// 		<span className={styles['order-qty']}>
			// 			{/* <s className={styles.actual}>{product?.orderedQty}</s> */}
			// 			<p>{product?.requiredQty}</p>
			// 			<IconButton
			// 				onClick={() => {
			// 					handleEditClick(product?.variantId);
			// 				}}
			// 				className={styles['edit-btn']}
			// 			>
			// 				<ModeEdit />
			// 			</IconButton>
			// 		</span>
			// 	);
			// }
			// return (
			// 	<span className={styles['order-qty']}>
			// 		<p>{recommendedValue}</p>
			// 		<IconButton
			// 			onClick={() => {
			// 				handleEditClick(product?.variantId);
			// 			}}
			// 			className={styles['edit-btn']}
			// 		>
			// 			<ModeEdit />
			// 		</IconButton>
			// 	</span>
			// );
			// return product?.updatedQty - (product?.orderedQty ?? 0);
		},
		MOQ: (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			return product?.moq;
		},
		'Rounded Off Ordered Quantity': (id) => {
			const product = listData?.find((prod) => {
				return prod?.variantId === id;
			});
			return product?.roundedOffQty;
		},
	};

	const handleClose = () => {
		setListData([]);
		setFinalItemList([]);
		setSearchQuery('');
		toggleGenerateStandardPOModal(false, {});
	};

	const handleConfirm = async () => {
		const temp = [];

		listData?.forEach((ele) => {
			if (finalItemList.includes(ele.variantId)) {
				const product = productsList?.find((prod) => {
					return prod?.id === ele?.productId;
				});
				let selectedVariant = product?.variants?.find((vrnt) => {
					return vrnt?.id === ele?.variantId;
				});
				if (!selectedVariant) {
					return;
				}
				selectedVariant = JSON.parse(JSON.stringify(selectedVariant));
				selectedVariant.price = getVarPrice(product, selectedVariant);
				if (product?.unit === 'gram' || product?.unit === 'ml') {
					selectedVariant.price *= 1000;
				}
				if (product?.unit === 'oz') {
					selectedVariant.price *= 16;
				}
				if (product?.unit === 'floz') {
					selectedVariant.price *= 128;
				}
				const subTotal = config.getSubTotal(
					selectedVariant?.price,
					ele?.discountPercentage ?? 0,
					ele?.roundedOffQty,
					ele?.unit
				);
				const totalTax = config.getTotalTax(
					selectedVariant?.price,
					ele?.discountPercentage ?? 0,
					product?.tax ?? 0,
					ele?.roundedOffQty,
					ele?.unit
				);
				const totalDiscount = config.getTotalDiscount(
					selectedVariant?.price,
					ele?.discountPercentage ?? 0,
					ele?.roundedOffQty,
					ele?.unit
				);
				temp?.push({
					id: selectedVariant?.id,
					productId: product?.id,
					productName: product?.productName,
					productImageUrl: product?.productImageUrl,
					variants: product?.variants,
					quantity: ele?.roundedOffQty,
					generatedQty: ele?.roundedOffQty,
					unit: product?.unit,
					productInputUnit: ele?.unit,
					selectedVariant,
					skuType: product?.skuType,
					tax: product?.tax,
					discountPercentage: ele?.discountPercentage ?? 0,
					subTotal,
					totalTax,
					totalDiscount,
					totalPriceIncTax: config.getTotalPrice(subTotal, totalTax),
				});
			}
		});
		setPoState([...poState, ...temp]);
		handleClose();
	};

	const handleExport = () => {
		const columns = SPECIAL_COLUMNS.filter((c) => c.title !== 'Select');
		const headers = ['Selected', ...columns.map((c) => c.title)];

		const safe = (v) => (v === null || v === undefined || v === '' ? '-' : v);

		const rows = (DATA ?? []).map((row) => {
			const isSelected = finalItemList?.includes(row?.variantId) ? 'Yes' : 'No';

			const values = columns.map((col) => {
				if (col.title === 'Required Quantity') {
					return safe(row?.requiredQty ?? row?.recommendedOrderQty);
				}
				const rendered = SPECIAL_ITEMS[col.title]?.(row?.variantId);
				if (typeof rendered === 'string' || typeof rendered === 'number') {
					return safe(rendered);
				}
				const fieldKey = COLMAP[col.title];
				return safe(row?.[fieldKey]);
			});

			return [isSelected, ...values];
		});

		const fileName = `Standard PO${data?.shiftName ? ` - ${data.shiftName}` : ''}${moment(new Date()).format('DD-MM-YYYY')}`;
		handleExcelDownload(headers, rows, fileName, true);
	};

	const renderBtns = () => {
		return (
			!data?.hideButtons && (
				<div className={styles.btns}>
					<Button
						onClick={handleClose}
						variant='outline'
						className='flex-1'
					>
						Close
					</Button>
					<Button
						onClick={handleConfirm}
						className='w-full flex-1 primary-btn'
					>
						Add Items
					</Button>
				</div>
			)
		);
	};

	// eslint-disable-next-line no-unused-vars
	const getRowStyles = (ele) => {
		return '';
	};

	// listData is already decorated + sorted by the effect above, so DATA
	// just filters by search and exposes `id` for the table.
	const DATA = useMemo(() => {
		return (listData ?? [])
			.filter((ele) => (
				ele?.productName
					?.toLowerCase()
					?.indexOf(searchQuery?.toLowerCase()?.trim()) !== -1
			))
			.map((ele) => ({
				...ele,
				id: ele?.variantId,
			}));
	}, [listData, searchQuery]);

	const noteInfo = () => {
		if (data?.shiftName) {
			return (
				<div className='flex flex-1 w-[80%] justify-center items-center p-3 bg-orange-100 border border-orange-500 rounded-md'>
					<p>Recommended order items are based on shift menu</p>
				</div>
			);
		}
		return (
			<div className='flex flex-1 w-[80%] justify-start items-center p-3 bg-orange-100 border border-orange-500 rounded-md'>
				<p className='flex flex-col gap-2'>
					<span>CS-Current Stock,OF - Ordering Frequency(last 10 Days), DC - Daily Consumption(last 7 Days)</span>
					<span>TH- Threshold, POQ-Pending Receiving(same source),LD- Lead days</span>
					<span>Recommended Qty = MAX(OF*DC+LD*DC+TH - IF((CS+POQ)-LD*DC&gt;0,CS+POQ,LD*DC)),0)</span>
				</p>
			</div>
		);
	};
	return (
		<BaseModal
			heading='Generated Standard PO'
			open={show}
			handleClose={handleClose}
			customStyles={{
				width: '95%',
				height: '90vh',
			}}
			renderBtns={renderBtns}
			showNote={true}
			noteInfo={noteInfo}
		>
			<div className={`${styles.root}`}>
				<div className={styles.filters}>
					<div className='flex items-center gap-4'>
						<div className='flex flex-col items-start gap-1'>
							<Label>Search SKU</Label>
							<Input
								className='min-w-80 w-fit'
								value={searchQuery}
								onChange={(e) => {
									setSearchQuery(e?.target?.value);
								}}
								placeholder='Search SKU'
							/>
						</div>
						<div className='flex flex-col items-start gap-1'>
							<Label>&nbsp;</Label>
							<Button
								variant='outline'
								onClick={handleSelectAll}
							>
								{finalItemList?.length === listData?.length ? 'Deselect All' : 'Select All'}
							</Button>
						</div>
					</div>
				</div>

				<div className={styles.tableContainer}>
					<ListTable
						colMap={COLMAP}
						columns={[]}
						data={DATA}
						actions={[...SPECIAL_COLUMNS]}
						special={SPECIAL_ITEMS}
						fixedHeaderTop="0px"
						allowExport
						onExportClick={handleExport}
					/>
				</div>
			</div>
		</BaseModal>
	);
};

const mapStateToProps = (state) => {
	const { show, data } = state.modal.generateStandardPoModal;
	const { LIST: productsList } = state.form.ALL_PRODUCTS.data;

	return {
		show,
		data,
		productsList,
	};
};

const mapDispatchToProps = {
	toggleGenerateStandardPOModal: modalActions.toggleGenerateStandardPOModal,
};
export default connect(mapStateToProps, mapDispatchToProps)(GenerateStandardPo);
